import React, { useMemo } from "react";
import PropTypes from "prop-types";
import GoogleMap from "components/core/GoogleMap";
import Column from "components/core/Page/Column";
import FacilityCard from "components/organisms/search/FacilityCards/FacilityCard";
import { facilityCardsToMapLocations } from "components/google-maps";

/** Map column for SearchPageV2: map, loader overlay, move message, and facility card overlay. */
export default function SearchPageV2MapColumn({
	showMapView,
	mapCenter,
	facilityCards,
	handleMapMove,
	handleMapLocationClick,
	showMapLoader,
	mapMoveMessage,
	searchFromMove,
	setSearchFromMove,
	selectedFacilityId,
	hoveredFacilityId,
	setHoveredFacilityId,
	mapRef,
	googleMapsRef,
	selectedCardForOverlay,
	isOverlayClosing,
	onOverlayTransitionEnd,
	locationName
}) {
	// Avoid recomputing map locations on every render when facilityCards is unchanged
	const mapLocations = useMemo(
		() => facilityCardsToMapLocations(facilityCards),
		[facilityCards]
	);

	return (
		<Column
			className={`sidebar ${
				showMapView ? "show-map-view" : "hide-map-view"
			}`}
			small={12}
			medium={7}
			mediumLarge={6}
			gutter="xlarge"
		>
			<div className="map-container">
				{/* When JavaScript is disabled, show a list of facilities with links instead of the map */}
				<noscript>
					<div className="map-container__facilities-list">
						<h2 className="map-container__facilities-list-title">
							Facilities
						</h2>
						<ul className="map-container__facilities-list-items">
							{facilityCards.map((facility) => (
								<li key={facility.id}>
									<a href={facility.url}>{facility.name}</a>
								</li>
							))}
						</ul>
					</div>
				</noscript>
				{mapCenter && (
					<>
						<GoogleMap
							center={mapCenter}
							locations={mapLocations}
							markerType="overlay"
							radius={15}
							onMove={handleMapMove}
							onLocationClick={handleMapLocationClick}
							onLocationHover={(loc) =>
								setHoveredFacilityId?.(loc?.id ?? null)
							}
							onMapReady={(map, google) => {
								mapRef.current = map;
								googleMapsRef.current = google;
							}}
							searchFromMove={searchFromMove}
							setSearchFromMove={setSearchFromMove}
							selectedLocationId={selectedFacilityId}
							hoveredLocationId={hoveredFacilityId}
							ariaLabel={
								locationName
									? `An interactive map displaying storage facilities in ${locationName}`
									: undefined
							}
							renderOverlay={(loc) => {
								const price = loc.label
									? Math.floor(
											Number.parseFloat(
												loc.label.replace(/[$,]/g, "")
											)
										)
									: Number.NaN;
								return (
									<span className="map-price-label">
										{Number.isNaN(price)
											? loc.label || ""
											: `$${price}`}
									</span>
								);
							}}
						/>
						{mapMoveMessage && !showMapLoader && (
							<div
								className="map-move-message"
								role="status"
								aria-live="polite"
							>
								{mapMoveMessage}
							</div>
						)}
					</>
				)}
				{selectedCardForOverlay && (
					<div
						className={`search-map-overlay${isOverlayClosing ? " search-map-overlay--closing" : ""}`}
						role="dialog"
						aria-label="Selected facility"
					>
						<div
							className="search-map-overlay-card"
							onTransitionEnd={onOverlayTransitionEnd}
						>
							<FacilityCard
								layout="narrow"
								{...selectedCardForOverlay}
								hasOnlineMoveins={
									selectedCardForOverlay.hasOnlineMoveins
								}
							/>
						</div>
					</div>
				)}
			</div>
		</Column>
	);
}

SearchPageV2MapColumn.propTypes = {
	showMapView: PropTypes.bool.isRequired,
	mapCenter: PropTypes.shape({
		latitude: PropTypes.number,
		longitude: PropTypes.number
	}),
	facilityCards: PropTypes.arrayOf(PropTypes.object).isRequired,
	handleMapMove: PropTypes.func.isRequired,
	handleMapLocationClick: PropTypes.func.isRequired,
	showMapLoader: PropTypes.bool.isRequired,
	mapMoveMessage: PropTypes.string,
	searchFromMove: PropTypes.bool.isRequired,
	setSearchFromMove: PropTypes.func.isRequired,
	selectedFacilityId: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number
	]),
	hoveredFacilityId: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number
	]),
	setHoveredFacilityId: PropTypes.func,
	mapRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
	googleMapsRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
	selectedCardForOverlay: PropTypes.object,
	isOverlayClosing: PropTypes.bool,
	onOverlayTransitionEnd: PropTypes.func,
	locationName: PropTypes.string
};
